import { Component, inject, OnInit, signal } from '@angular/core';
import { Events, AppEvent } from '../event';
import { Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { firstValueFrom } from 'rxjs';
import { DatePipe } from '@angular/common';
import { openPresentationWindow } from '../presentation';
import { ToastService } from '../toast';

@Component({
  selector: 'app-admin',
  standalone: true,
  imports: [RouterModule, MatIconModule, DatePipe],
  template: `
    <div class="min-h-screen bg-[#fafafa] text-slate-900 font-sans selection:bg-black selection:text-white">
      @if (!adminAccessGranted()) {
        <div class="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/75 p-5 backdrop-blur-sm">
          <div class="w-full max-w-md rounded-[2rem] bg-white p-8 shadow-2xl">
            <div class="mb-7">
              <img src="/logostudiobooth.png" alt="StudioBooth" class="mb-5 h-14 w-auto object-contain" />
              <p class="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Acceso administrativo</p>
              <h2 class="mt-2 whitespace-nowrap text-[clamp(1.65rem,7vw,2rem)] font-black italic uppercase tracking-tighter text-slate-900">Central de Activaciones</h2>
            </div>
            <input type="password" inputmode="numeric" autocomplete="off" placeholder="PIN de acceso"
                   [value]="adminPin()" (input)="adminPin.set($any($event.target).value)" (keyup.enter)="unlockAdmin()"
                   class="w-full rounded-2xl border border-slate-200 bg-slate-50 p-5 text-lg font-bold text-slate-900 outline-none focus:ring-2 focus:ring-slate-700">
            <button type="button" (click)="unlockAdmin()" [disabled]="loadingEvents()"
                    class="mt-8 w-full rounded-2xl bg-black py-4 text-[10px] font-black uppercase tracking-widest text-white disabled:opacity-50">
              {{ loadingEvents() ? 'Verificando...' : 'Entrar' }}
            </button>
          </div>
        </div>
      }

      <div class="max-w-7xl mx-auto px-6 py-12">
        <header class="flex flex-col md:flex-row md:items-end justify-between mb-16 border-b-2 border-slate-900/5 pb-10 gap-6">
          <div>
            <div class="flex items-center gap-3 mb-2">
              <span class="w-3 h-3 bg-black rounded-full shadow-[0_0_10px_rgba(0,0,0,0.2)]"></span>
              <span class="text-[10px] font-black tracking-[0.3em] uppercase text-slate-400 italic">Central de Activaciones</span>
            </div>
            <img src="/logostudiobooth.png" alt="StudioBooth" class="h-20 w-auto max-w-[360px] object-contain" />
          </div>
          
          <button (click)="createEvent()" class="group relative px-8 py-4 bg-black text-white rounded-2xl text-[11px] font-black italic uppercase tracking-widest transition-all hover:scale-105 active:scale-95 shadow-2xl border border-white/10 group" [hidden]="adminMode() !== 'superadmin'">
            <div class="relative flex items-center gap-3">
              <mat-icon class="text-[20px] w-[20px] h-[20px]">add_circle</mat-icon>
              <span>Nueva Activación</span>
            </div>
          </button>
          @if (adminMode() === 'event') {
            <div class="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-[10px] font-black uppercase tracking-widest text-slate-500 shadow-sm">
              Acceso Cliente
            </div>
          }
          @if (adminAccessGranted()) {
            <button type="button" (click)="logout()" title="Cerrar sesión" class="h-11 w-11 rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition-all hover:text-slate-950 hover:shadow-md active:scale-95">
              <mat-icon class="text-[20px]">logout</mat-icon>
            </button>
          }
        </header>

        @if (events().length === 0) {
          <div class="py-32 border-4 border-dashed border-slate-100 rounded-[40px] flex flex-col items-center justify-center text-center bg-white">
            <div class="w-20 h-20 bg-white shadow-xl rounded-3xl flex items-center justify-center mb-6 transform -rotate-6 border border-slate-100">
              <mat-icon class="text-slate-200 text-4xl">layers</mat-icon>
            </div>
            <h2 class="text-2xl font-black italic uppercase tracking-tighter text-slate-400 mb-2">Espacio Vacío</h2>
            <p class="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-10 max-w-xs">Comienza creando tu primera motor de fotos para el evento.</p>
            <button (click)="createEvent()" [hidden]="adminMode() !== 'superadmin'" class="px-10 py-4 border-2 border-slate-900 text-slate-900 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-black hover:text-white transition-all shadow-lg active:scale-95 italic">
              Iniciar registro ahora
            </button>
          </div>
        } @else {
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            @for (evt of events(); track evt.id) {
              <div class="group flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div class="aspect-[16/10] bg-white rounded-[32px] mb-6 overflow-hidden relative border border-slate-100 shadow-sm transition-all duration-500 hover:shadow-2xl hover:-translate-y-2">
                  <div class="absolute inset-0 p-8 flex items-center justify-center opacity-60 grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700">
                    @if (evt.frameUrl) {
                      <img [src]="evt.frameUrl" [alt]="evt.title" class="w-full h-full object-contain filter drop-shadow-2xl" crossorigin="anonymous" />
                    } @else {
                      <mat-icon class="text-slate-100 text-[120px] w-auto h-auto">photo_frame</mat-icon>
                    }
                  </div>

                  <!-- Overlay Info -->
                  <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-8">
                     <p class="text-white text-[10px] font-black uppercase tracking-widest italic mb-1">Última captura</p>
                     <p class="text-white/60 text-[9px] font-bold uppercase tracking-widest">{{ evt.createdAt | date:'short' }}</p>
                  </div>
                  
                  <div class="absolute top-6 right-6 flex items-center gap-2">
                    <button type="button" (click)="requestDelete(evt)" title="Eliminar" [hidden]="adminMode() !== 'superadmin'" class="w-12 h-12 bg-white shadow-xl rounded-2xl flex items-center justify-center text-red-600 transition-all hover:scale-110 active:scale-90 border border-red-100">
                      <mat-icon class="text-[20px]">delete</mat-icon>
                    </button>
                    <button type="button" (click)="openEditor(evt)" title="Editar" class="w-12 h-12 bg-white shadow-xl rounded-2xl flex items-center justify-center text-slate-900 transition-all hover:scale-110 active:scale-90 border border-slate-100">
                      <mat-icon class="text-[20px]">tune</mat-icon>
                    </button>
                  </div>
                </div>

                <div class="px-2">
                  <div class="flex items-start justify-between gap-4 mb-6">
                    <div class="min-w-0">
                      <h3 class="text-2xl font-black italic uppercase italic tracking-tighter text-slate-900 truncate">{{ evt.title }}</h3>
                      <div class="flex items-center gap-3 mt-1.5">
                        <span class="text-[9px] font-black uppercase tracking-widest text-slate-500 italic">{{ evt.createdAt | date:'MMM d, yyyy' }}</span>
                        <span class="w-1 h-1 bg-slate-300 rounded-full"></span>
                        <span class="flex items-center gap-1.5 bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest">
                          <span class="w-1 h-1 bg-emerald-500 rounded-full animate-pulse"></span>
                          Online
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <button type="button" (click)="openPresentation(evt, 'selfie')" title="Selfie" class="group h-10 w-10 flex items-center justify-center bg-slate-950 text-white rounded-lg transition-all hover:bg-black active:scale-95 shadow-sm border border-slate-950">
                      <mat-icon class="text-[18px] text-emerald-400">smartphone</mat-icon>
                    </button>
                    <button type="button" (click)="openPresentation(evt, 'selfieAccess')" title="QR Selfie" class="group h-10 w-10 flex items-center justify-center bg-white text-slate-700 rounded-lg transition-all hover:text-slate-950 hover:bg-slate-50 hover:shadow-sm active:scale-95 border border-slate-200">
                      <mat-icon class="text-[18px] text-slate-400">qr_code_2</mat-icon>
                    </button>
                    <button type="button" (click)="openPresentation(evt, 'kiosk')" title="Kiosk" class="group h-10 w-10 flex items-center justify-center bg-white text-slate-700 rounded-lg transition-all hover:text-slate-950 hover:bg-slate-50 hover:shadow-sm active:scale-95 border border-slate-200">
                      <mat-icon class="text-[18px] text-slate-400">camera_alt</mat-icon>
                    </button>
                    <button type="button" (click)="openPresentation(evt, 'gallery')" title="Mural" class="group h-10 w-10 flex items-center justify-center bg-white text-slate-700 rounded-lg transition-all hover:text-slate-950 hover:bg-slate-50 hover:shadow-sm active:scale-95 border border-slate-200">
                      <mat-icon class="text-[18px] text-slate-400">grid_view</mat-icon>
                    </button>
                    <button type="button" (click)="openModeration(evt)" title="Moderar" class="group h-10 w-10 flex items-center justify-center bg-white border border-slate-200 rounded-lg text-slate-700 hover:text-slate-950 hover:bg-slate-50 hover:shadow-sm transition-all active:scale-95 shrink-0">
                      <mat-icon class="text-[18px]">verified_user</mat-icon>
                    </button>
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>

      @if (showCreatePinModal()) {
        <div class="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-5 backdrop-blur-sm">
          <div class="w-full max-w-md rounded-[2rem] bg-white p-8 shadow-2xl">
            <div class="mb-7">
              <p class="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Seguridad temporal</p>
              <h2 class="mt-2 text-3xl font-black italic uppercase tracking-tighter text-slate-900">PIN de edición</h2>
              <p class="mt-2 text-sm font-medium leading-relaxed text-slate-500">Este PIN se pedirá para personalizar la activación.</p>
            </div>
            <div class="space-y-4">
              <input type="password" inputmode="numeric" autocomplete="new-password" placeholder="PIN de la activación"
                     [value]="createPin()" (input)="createPin.set($any($event.target).value)"
                     class="w-full rounded-2xl border border-slate-200 bg-slate-50 p-5 text-lg font-bold text-slate-900 outline-none focus:ring-2 focus:ring-slate-700">
              <input type="password" inputmode="numeric" autocomplete="new-password" placeholder="Confirmar PIN"
                     [value]="createPinConfirm()" (input)="createPinConfirm.set($any($event.target).value)"
                     class="w-full rounded-2xl border border-slate-200 bg-slate-50 p-5 text-lg font-bold text-slate-900 outline-none focus:ring-2 focus:ring-slate-700">
            </div>
            <div class="mt-8 flex gap-3">
              <button type="button" (click)="closeCreatePinModal()" class="flex-1 rounded-2xl border border-slate-200 bg-white py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Cancelar</button>
              <button type="button" (click)="confirmCreateEvent()" [disabled]="isCreating()" class="flex-[1.5] rounded-2xl bg-black py-4 text-[10px] font-black uppercase tracking-widest text-white disabled:opacity-50">
                {{ isCreating() ? 'Creando...' : 'Crear activación' }}
              </button>
            </div>
          </div>
        </div>
      }

      @if (deleteCandidate()) {
        <div class="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-5 backdrop-blur-sm">
          <div class="w-full max-w-md rounded-[2rem] bg-white p-8 shadow-2xl">
            <div class="mb-7">
              <p class="text-[10px] font-black uppercase tracking-[0.3em] text-red-400">Eliminar activación</p>
              <h2 class="mt-2 text-3xl font-black italic uppercase tracking-tighter text-slate-900">{{ deleteCandidate()?.title }}</h2>
              <p class="mt-2 text-sm font-medium leading-relaxed text-slate-500">Para borrar la activación y sus fotos se requiere el PIN de superadministrador.</p>
            </div>
            <input type="password" inputmode="numeric" autocomplete="off" placeholder="PIN superadmin"
                   [value]="deletePin()" (input)="deletePin.set($any($event.target).value)"
                   class="w-full rounded-2xl border border-slate-200 bg-slate-50 p-5 text-lg font-bold text-slate-900 outline-none focus:ring-2 focus:ring-slate-700">
            <div class="mt-8 flex gap-3">
              <button type="button" (click)="closeDeleteModal()" class="flex-1 rounded-2xl border border-slate-200 bg-white py-4 text-[10px] font-black uppercase tracking-widest text-slate-500">Cancelar</button>
              <button type="button" (click)="confirmDeleteEvent()" [disabled]="isDeleting()" class="flex-[1.5] rounded-2xl bg-red-600 py-4 text-[10px] font-black uppercase tracking-widest text-white disabled:opacity-50">
                {{ isDeleting() ? 'Eliminando...' : 'Eliminar' }}
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  `
})
export class Admin implements OnInit {
  events = signal<AppEvent[]>([]);
  showCreatePinModal = signal(false);
  createPin = signal('');
  createPinConfirm = signal('');
  isCreating = signal(false);
  deleteCandidate = signal<AppEvent | null>(null);
  deletePin = signal('');
  isDeleting = signal(false);
  adminAccessGranted = signal(false);
  adminPin = signal('');
  adminMode = signal<'superadmin' | 'event' | null>(null);
  loadingEvents = signal(false);
  eventsService = inject(Events);
  router = inject(Router);
  toast = inject(ToastService);

  async ngOnInit() {
    const storedPin = sessionStorage.getItem('studiobooth:admin-pin') || '';
    if (!storedPin) return;
    this.adminPin.set(storedPin);
    await this.unlockAdmin(false);
  }

  async createEvent() {
    if (this.adminMode() !== 'superadmin') {
      this.toast.error('Solo el superadmin puede crear activaciones.');
      return;
    }
    this.showCreatePinModal.set(true);
  }

  closeCreatePinModal() {
    this.showCreatePinModal.set(false);
    this.createPin.set('');
    this.createPinConfirm.set('');
  }

  async confirmCreateEvent() {
    const pin = this.createPin().trim();
    if (pin.length < 4) {
      this.toast.error('Define un PIN de al menos 4 digitos.');
      return;
    }
    if (pin !== this.createPinConfirm().trim()) {
      this.toast.error('Los PIN no coinciden.');
      return;
    }
    this.isCreating.set(true);
    try {
      const newEvent = await firstValueFrom(this.eventsService.createEvent({ title: 'Nueva Activación', editPin: pin }));
      sessionStorage.setItem(`studiobooth:event-pin:${newEvent.id}`, pin);
      this.closeCreatePinModal();
      this.router.navigate(['/admin/event', newEvent.id]);
    } catch {
      this.toast.error('No se pudo crear la activación.');
    } finally {
      this.isCreating.set(false);
    }
  }

  async unlockAdmin(showErrors = true) {
    const pin = this.adminPin().trim();
    if (!pin) {
      if (showErrors) this.toast.error('Ingresa el PIN superadmin.');
      return;
    }
    this.loadingEvents.set(true);
    try {
      const access = await firstValueFrom(this.eventsService.adminAccess(pin));
      this.events.set(access.events);
      this.adminMode.set(access.mode);
      this.adminAccessGranted.set(true);
      sessionStorage.setItem('studiobooth:admin-pin', pin);
      access.events.forEach(evt => sessionStorage.setItem(`studiobooth:event-pin:${evt.id}`, pin));
    } catch {
      this.adminAccessGranted.set(false);
      this.adminMode.set(null);
      sessionStorage.removeItem('studiobooth:admin-pin');
      if (showErrors) this.toast.error('PIN superadmin incorrecto.');
    } finally {
      this.loadingEvents.set(false);
    }
  }

  openEditor(evt: AppEvent) {
    this.router.navigate(['/admin/event', evt.id]);
  }

  openModeration(evt: AppEvent) {
    this.router.navigate(['/admin/moderate', evt.id]);
  }

  requestDelete(evt: AppEvent) {
    if (this.adminMode() !== 'superadmin') {
      this.toast.error('Solo el superadmin puede eliminar activaciones.');
      return;
    }
    this.deleteCandidate.set(evt);
    this.deletePin.set(this.adminPin().trim());
  }

  closeDeleteModal() {
    this.deleteCandidate.set(null);
    this.deletePin.set('');
  }

  async confirmDeleteEvent() {
    const target = this.deleteCandidate();
    if (!target) return;
    if (this.adminMode() !== 'superadmin') {
      this.toast.error('Solo el superadmin puede eliminar activaciones.');
      return;
    }
    this.isDeleting.set(true);
    try {
      await firstValueFrom(this.eventsService.deleteEvent(target.id, this.deletePin().trim() || this.adminPin().trim()));
      this.events.set(this.events().filter(evt => evt.id !== target.id));
      this.closeDeleteModal();
      this.toast.success('Activación eliminada.');
    } catch (error: any) {
      const message = error?.status === 401
        ? 'PIN de superadministrador incorrecto.'
        : (error?.error?.error || 'No se pudo eliminar la activación.');
      this.toast.error(message);
    } finally {
      this.isDeleting.set(false);
    }
  }

  logout() {
    sessionStorage.removeItem('studiobooth:admin-pin');
    for (const evt of this.events()) {
      sessionStorage.removeItem(`studiobooth:event-pin:${evt.id}`);
    }
    this.events.set([]);
    this.adminPin.set('');
    this.deletePin.set('');
    this.adminMode.set(null);
    this.adminAccessGranted.set(false);
    this.closeDeleteModal();
  }
  openPresentation(event: AppEvent, mode: 'selfie' | 'selfieAccess' | 'kiosk' | 'gallery') {
    const eventId = event.slug || event.id;
    const path = mode === 'kiosk' ? `/e/${eventId}` : mode === 'selfieAccess' ? `/e/${eventId}/selfie-access` : `/e/${eventId}/${mode}`;
    openPresentationWindow(path);
  }
}
